import { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useOutlineById,
  useAddOutlineNodeStandalone,
  useDeleteOutlineNodeStandalone,
  useUpdateOutlineNode,
  useOutlineCharacters,
  useAddOutlineCharacter,
  useRemoveOutlineCharacter,
  type OutlineDto,
} from '../api/queries/outlines';
import {
  useConflicts,
  useCreateConflict,
  useDeleteConflict,
} from '../api/queries/conflicts';
import { OutlineToolbar } from '../components/outline/OutlineToolbar';
import { OutlineTreeView } from '../components/outline/OutlineTreeView';
import { OutlineCardView } from '../components/outline/OutlineCardView';
import { OutlineNodeDetailPanel } from '../components/outline/OutlineNodeDetailPanel';
import { CharacterSection } from '../components/characters/CharacterSection';
import { ConflictSection } from '../components/characters/ConflictSection';
import { VersionPicker } from '../components/outline/VersionPicker';

export function OutlineDetailRoute() {
  const { outlineId } = useParams({ strict: false }) as {
    outlineId: string;
  };
  const navigate = useNavigate();
  const { data: outline, isLoading, isError } = useOutlineById(outlineId);

  if (isLoading) return <CircularProgress />;
  if (isError || !outline)
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">Outline not found or failed to load.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate({ to: '/outlines' })}>
          Back to outlines
        </Button>
      </Paper>
    );

  return (
    <div>
      {outline.projectId && (
        <VersionPicker
          projectId={outline.projectId}
          currentOutlineId={outlineId}
        />
      )}
      {!outline.projectId && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: 3, mt: 2 }}
        >
          <IconButton onClick={() => navigate({ to: '/outlines' })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {outline.name}
          </Typography>
        </Stack>
      )}

      {outline.projectId && (
        <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>
          {outline.name}
        </Typography>
      )}

      <StandaloneOutlineEditor outlineId={outlineId} outline={outline} />
    </div>
  );
}

function StandaloneOutlineEditor({
  outlineId,
  outline,
}: {
  outlineId: string;
  outline: OutlineDto;
}) {
  const addNode = useAddOutlineNodeStandalone(outlineId);
  const deleteNode = useDeleteOutlineNodeStandalone(outlineId);
  const updateNode = useUpdateOutlineNode(outlineId);

  // Characters
  const { data: characters = [] } = useOutlineCharacters(outlineId);
  const addCharacter = useAddOutlineCharacter(outlineId);
  const removeCharacter = useRemoveOutlineCharacter(outlineId);

  // Conflicts
  const { data: conflicts = [] } = useConflicts({ outlineId });
  const createConflict = useCreateConflict();
  const deleteConflict = useDeleteConflict();

  // View state
  const [viewMode, setViewMode] = useState<'tree' | 'cards'>('tree');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(
    () => new Set(outline.nodes.map((n) => n.id)),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [newName, setNewName] = useState('');

  const selectedNode = useMemo(
    () => outline.nodes.find((n) => n.id === selectedNodeId) ?? null,
    [outline.nodes, selectedNodeId],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedNodeIds(new Set(outline.nodes.map((n) => n.id)));
  }, [outline.nodes]);

  const collapseAll = useCallback(() => {
    setExpandedNodeIds(new Set());
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Card>
          <CardContent>
            <OutlineToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="New top-level node"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newName.trim()) {
                    addNode.mutateAsync({
                      outlineId: outline.id,
                      parentId: selectedNodeId,
                      name: newName.trim(),
                    });
                    setNewName('');
                  }
                }}
              />
              <Button
                variant="contained"
                disabled={!newName.trim() || addNode.isPending}
                onClick={async () => {
                  await addNode.mutateAsync({
                    outlineId: outline.id,
                    parentId: selectedNodeId,
                    name: newName.trim(),
                  });
                  setNewName('');
                }}
              >
                Add
              </Button>
            </Stack>

            {viewMode === 'tree' ? (
              <OutlineTreeView
                nodes={outline.nodes}
                selectedNodeId={selectedNodeId}
                expandedNodeIds={expandedNodeIds}
                searchQuery={searchQuery}
                onSelectNode={setSelectedNodeId}
                onToggleExpand={toggleExpand}
                onRenameNode={(id, name) => updateNode.mutate({ id, name })}
              />
            ) : (
              <OutlineCardView
                nodes={outline.nodes}
                selectedNodeId={selectedNodeId}
                searchQuery={searchQuery}
                onSelectNode={setSelectedNodeId}
              />
            )}

            {outline.nodes.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                No nodes yet. Add your first outline node above.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 2 }}>
          <CharacterSection
            characters={characters}
            onAdd={(entityId, role) => addCharacter.mutate({ entityId, role })}
            onRemove={(id) => removeCharacter.mutate(id)}
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <ConflictSection
            conflicts={conflicts}
            onCreate={(input) => createConflict.mutate({ ...input, outlineId })}
            onDelete={(id) => deleteConflict.mutate(id)}
          />
        </Box>
      </Box>

      <OutlineNodeDetailPanel
        node={selectedNode}
        open={!!selectedNode}
        onClose={() => setSelectedNodeId(null)}
        onSave={(updates) => updateNode.mutate(updates)}
        onDelete={(id) => {
          deleteNode.mutate(id);
          setSelectedNodeId(null);
        }}
      />
    </Box>
  );
}
